import {
  mountOnboardingForm,
  priorityOptions,
} from "components/onboarding_form";

const professionalClientName = "kyb_professional_client";
const generalName = "kyb_general";
const anyLegalProceedingsName = `${generalName}.any_legal_proceedings`;
const legalProceedingsName = `${generalName}.legal_proceedings`;
const anySanctionsName = `${generalName}.any_sanctions`;
const sanctionsName = `${generalName}.sanctions`;
const businessName = "kyb_business";
const businessAnyDegreesName = `${businessName}.any_degrees`;
const businessDegreesName = `${businessName}.degrees`;
const businessDegreeDefaultValue = {
  institution: "",
  degree: "",
  date: "",
  documents: [],
};
const businessAnyTradingHistoryName = `${businessName}.any_trading_history`;
const businessTradingHistoryName = `${businessName}.trading_history`;
const deemedName = `${professionalClientName}.deemed`;
const anyExperienceName = `${professionalClientName}.any_experience`;
const financialName = `${professionalClientName}.financial`;
const anyRelatedName = `${professionalClientName}.any_related`;

const stages = [
  {
    title: "General Information",
    fields: [
      {
        label: "Purpose and type of your business relationship with the Fund",
        name: `${generalName}.purpose`,
        type: "textarea",
        paragraph:
          "Investment, long-term savings, income generation, hedging, etc.",
        required: true,
      },
      {
        label: "Sources of income",
        name: `${generalName}.income_sources`,
        type: "textarea",
        paragraph:
          "Income from entrepreneurial activity, dividends, voluntary property contributions and donations, etc.",
        required: true,
      },
      {
        label: "Planned initial investment (equivalent in USD)",
        name: `${generalName}.investment`,
        type: "number",
        required: true,
        min: 50000,
      },
      {
        label: "Financial statements",
        name: `${generalName}.statements`,
        type: "documents",
        paragraph:
          "(P&L, Balance Sheet, Cash flow) for the last completed financial year, confirmed by an audit report",
        required: true,
      },
      {
        label: "Source of funds confirmation documents",
        name: `${generalName}.sow`,
        type: "documents",
        paragraph: "Documents and statements describing the origin of the funds",
        required: true,
      },
      {
        label:
          "Has the entity been involved in any litigation, arbitration, or other legal proceedings in the past five years?",
        name: anyLegalProceedingsName,
        type: "boolean_radiobutton",
        required: true,
      },
      {
        label: "Please provide details",
        name: legalProceedingsName,
        type: "textarea",
        required: true,
        showWhen: { name: anyLegalProceedingsName, value: "true" },
      },
      {
        label:
          "Has the Applicant, its shareholder(s)/ participant(s) (up to Beneficial Owners) or any member of its Group or any Director of the Applicant or a member of the Group, directly or indirectly, been designated as a target of, or is otherwise subject to, Sanctions?",
        name: anySanctionsName,
        type: "boolean_radiobutton",
        paragraph:
          '"Sanctions" means applicable economic or financial sanctions or restrictive measures adopted and enforced by the United Nations Security Council, governmental bodies with the regulatory authority of the European Union, the United Kingdom (including, but not limited to, through HM\'s Treasury), the United States (including, but not limited to, through the United States Office of Foreign Assets Control (OFAC)), the Republic of Kazakhstan, in each case, to the extent these measures are applicable.',
        required: true,
      },
      {
        label: "Please provide details",
        name: sanctionsName,
        type: "textarea",
        required: true,
        showWhen: { name: anySanctionsName, value: "true" },
      },
      {
        label:
          "Is any of the Applicant's Directors/Beneficial Owners/Authorized Signatory/or their Immediate Relative or Close Associate Individual a politically exposed person (PEP)?",
        name: `${generalName}.pep`,
        type: "radiobutton",
        paragraph:
          "PEP is a natural person (including a family member or known associate) who is or has been entrusted with a prominent public function, including but not limited to: a head of state or of government, senior politician, member of a legislative or constitutional assembly, senior government official, senior judicial official, senior military officer, ambassador, senior person in an international organisation, senior executive of a state-owned entity, a senior political party official, or an individual who has been entrusted with similar functions such as a director or a deputy director; at an international, national, or regional level.",
        required: true,
        options: [
          { label: "an official public person", value: "official" },
          { label: "spouse and close relatives", value: "spouse-or-relative" },
          { label: "no", value: "no" },
        ],
      },
      {
        label: "Supplementary documents",
        name: `${generalName}.supplementary`,
        type: "documents",
        paragraph: "Powers of attorney and other documents",
      },
      {
        label: "Comments for customer support",
        name: `${generalName}.comments`,
        type: "textarea",
        paragraph:
          "Any information or context you would like the customer support team to know about the documents you submitted",
      },
    ],
  },
  {
    title: "Business",
    fields: [
      {
        name: "kyb_business.segments",
        type: "array",
        paragraph: "Major segments of operation",
        addLabel: "Add segment",
        defaultValue: { name: "", length: "" },
        initialValue: [{ name: "", length: "" }],
        keepFirstWhenShown: true,
        requiredWhenShown: true,
        fields: [
          {
            label: "Segment",
            name: "name",
            type: "string",
            required: true,
          },
          {
            label: "Length of operation",
            name: "length",
            type: "string",
            required: true,
          },
        ],
      },
      {
        label: "Company’s responsible officer CV or other employment record",
        name: `${businessName}.cv`,
        type: "documents",
        required: true,
      },
      {
        label:
          "Does the company’s responsible officer have degrees or certificates in finance, economics, accounting, high math, or IT (translated to English and notarized)?",
        name: businessAnyDegreesName,
        type: "boolean_radiobutton",
        required: true,
      },
      {
        name: businessDegreesName,
        type: "array",
        showWhen: { name: businessAnyDegreesName, value: "true" },
        keepFirstWhenShown: true,
        requiredWhenShown: true,
        addLabel: "Add degree/certificate",
        defaultValue: businessDegreeDefaultValue,
        fields: [
          {
            label: "Institution",
            name: "institution",
            type: "string",
            required: true,
          },
          {
            label: "Degree or certificate name",
            name: "degree",
            type: "string",
            required: true,
          },
          {
            label: "Date of completion",
            name: "date",
            type: "date",
            required: true,
          },
          {
            label: "Diploma or certificate",
            name: "documents",
            type: "documents",
            required: true,
          },
        ],
      },
      {
        label: "Does the company have any trading history within the last 24 months?",
        name: businessAnyTradingHistoryName,
        type: "boolean_radiobutton",
        required: true,
      },
      {
        label: "Trading history",
        name: businessTradingHistoryName,
        type: "documents",
        required: true,
        showWhen: { name: businessAnyTradingHistoryName, value: "true" },
      },
    ],
  },
  {
    title: "Investment Objectives",
    fields: [
      {
        label: "Current income",
        name: "kyb_investment_objectives.capital_growth",
        type: "radiobutton",
        required: true,
        options: priorityOptions,
      },
      {
        label: "Income generation",
        name: "kyb_investment_objectives.income",
        type: "radiobutton",
        required: true,
        options: priorityOptions,
      },
      {
        label: "Capital preservation",
        name: "kyb_investment_objectives.capital_preservation",
        type: "radiobutton",
        required: true,
        options: priorityOptions,
      },
      {
        label: "Other",
        name: "kyb_investment_objectives.other",
        type: "textarea",
      },
    ],
  },
  {
    title: "Professional Client Status",
    paragraph:
      "The Fund and Administrator hereby provides you (the Investor) with a clear written warning that by giving up its classification as a Retail Client, the Investor will lose certain protections. These protections include, but may not be limited to, those afforded to Retail Clients under applicable regulations.",
    fields: [
      {
        label:
          "Please indicate whether your entity belongs to any of these categories:",
        name: deemedName,
        type: "radiobutton",
        required: true,
        options: [
          { label: "a national or regional government?", value: "national" },
          {
            label: "a public body that manages public debt?",
            value: "public_body",
          },
          { label: "a central bank?", value: "central_bank" },
          {
            label:
              "an international or supranational institution (e.g., World Bank, IMF, EIB)?",
            value: "international",
          },
          {
            label:
              "an Authorised Firm or any other authorised or regulated financial institution (e.g., bank, securities firm, insurance company)?",
            value: "authorised_firm",
          },
          {
            label:
              "an Authorised Market Institution or any other authorised or regulated exchange, trading facility, central securities depository, or clearing house?",
            value: "authorised_market",
          },
          {
            label: "a Collective Investment Scheme or its management company?",
            value: "collective",
          },
          {
            label: "a pension fund or the management company of a pension fund?",
            value: "pension_fund",
          },
          {
            label: "a commodity dealer or a commodity derivatives dealer?",
            value: "commodity_dealer",
          },
          {
            label:
              "a Large Undertaking (as specified in Section 2.4.2 of the AIFC Conduct of Business Rules)?",
            value: "large_undertaking",
          },
          {
            label:
              "a Body Corporate whose shares are listed or admitted to trading on any exchange of an IOSCO member country?",
            value: "body_corporate",
          },
          { label: "none of the above", value: "no" },
        ],
      },
      {
        label: "Please provide supporting documents",
        name: `${professionalClientName}.deemed_documents`,
        type: "documents",
        required: true,
        showWhen: { name: deemedName, notValue: "no", present: true },
      },
      {
        label:
          "Does your entity (or the individual(s) authorized to make investment decisions on behalf of your entity) have sufficient experience and understanding of the relevant Financial Products, Financial Services, or Transactions and any associated risks?",
        name: anyExperienceName,
        type: "boolean_radiobutton",
        required: true,
        showWhen: { name: deemedName, value: "no" },
      },
      {
        label: "Please provide details of relevant experience",
        name: `${professionalClientName}.experience`,
        type: "textarea",
        required: true,
        showWhen: [
          { name: deemedName, value: "no" },
          { name: anyExperienceName, value: "true" },
        ],
      },
      {
        label: "Please provide supporting documents",
        name: `${professionalClientName}.experience_documents`,
        type: "documents",
        required: true,
        showWhen: [
          { name: deemedName, value: "no" },
          { name: anyExperienceName, value: "true" },
        ],
      },
      {
        label: "Does your entity have own funds of at least USD 1 million?",
        name: financialName,
        type: "boolean_radiobutton",
        required: true,
        showWhen: { name: deemedName, value: "no" },
      },
      {
        label: "Please provide supporting documents",
        name: `${professionalClientName}.financial_documents`,
        type: "documents",
        required: true,
        showWhen: [
          { name: deemedName, value: "no" },
          { name: financialName, value: "true" },
        ],
      },
      {
        label:
          "Does your entity have a Controller, Holding Company, Subsidiary (direct or indirect), or joint venture partner that meets the requirements in COB 2.5.6 to be classified as an Assessed Professional Client?",
        name: anyRelatedName,
        type: "boolean_radiobutton",
        required: true,
        showWhen: { name: deemedName, value: "no" },
      },
      {
        label: "Please specify the relationship",
        name: `${professionalClientName}.related_relationship`,
        type: "textarea",
        required: true,
        showWhen: [
          { name: deemedName, value: "no" },
          { name: anyRelatedName, value: "true" },
        ],
      },
      {
        label: "Please provide supporting documents",
        name: `${professionalClientName}.related_documents`,
        type: "documents",
        required: true,
        showWhen: [
          { name: deemedName, value: "no" },
          { name: anyRelatedName, value: "true" },
        ],
      },
    ],
  },
  {
    title: "Manner of Solicitation",
    fields: [
      {
        label:
          "Please state the manner in which you became aware of the investment (for example, by personal contact or acquaintance with an investment advisor or counselor, with Fund Manager personnel, a broker-dealer, or otherwise):",
        name: "kyb_solicitation.manner",
        type: "string",
        required: true,
      },
      {
        label: "Name of the contact person",
        name: "kyb_solicitation.name",
        type: "string",
        required: true,
      },
      {
        label: "Date",
        name: "kyb_solicitation.date",
        type: "date",
        required: true,
      },
    ],
  },
];

const booleanRadiobuttonFieldNames = new Set([
  anyLegalProceedingsName,
  anySanctionsName,
  businessAnyDegreesName,
  businessAnyTradingHistoryName,
  anyExperienceName,
  financialName,
  anyRelatedName,
]);

function fieldIsHidden(name, values, getPath) {
  const deemed = getPath(values, deemedName);

  if (name === `${professionalClientName}.deemed_documents`) {
    return !deemed || deemed === "no";
  }

  if (
    [anyExperienceName, financialName, anyRelatedName].includes(name)
  ) {
    return deemed !== "no";
  }

  if (
    [
      `${professionalClientName}.experience`,
      `${professionalClientName}.experience_documents`,
    ].includes(name)
  ) {
    return deemed !== "no" || getPath(values, anyExperienceName) !== "true";
  }

  if (name === `${professionalClientName}.financial_documents`) {
    return deemed !== "no" || getPath(values, financialName) !== "true";
  }

  if (
    [
      `${professionalClientName}.related_relationship`,
      `${professionalClientName}.related_documents`,
    ].includes(name)
  ) {
    return deemed !== "no" || getPath(values, anyRelatedName) !== "true";
  }

  return false;
}

const config = {
  stages,
  booleanRadiobuttonFieldNames,
  normalizeFieldValue(name, value, values) {
    const getPath = (object, path) =>
      path.split(".").reduce((current, key) => current?.[key], object);

    if (
      (name === legalProceedingsName &&
        getPath(values, anyLegalProceedingsName) !== "true") ||
      (name === sanctionsName &&
        getPath(values, anySanctionsName) !== "true")
    ) {
      return { handled: true, value: undefined };
    }

    if (
      (name === businessDegreesName &&
        getPath(values, businessAnyDegreesName) !== "true") ||
      (name === businessTradingHistoryName &&
        getPath(values, businessAnyTradingHistoryName) !== "true")
    ) {
      return { handled: true, value: [] };
    }

    if (!name.startsWith(`${professionalClientName}.`)) return null;

    if (fieldIsHidden(name, values, getPath)) {
      return {
        handled: true,
        value: name.endsWith("_documents") ? [] : undefined,
      };
    }

    return null;
  },
  onFieldChange({ clearErrors, getPath, name, setValue, values }) {
    [
      [anyLegalProceedingsName, legalProceedingsName],
      [anySanctionsName, sanctionsName],
    ].forEach(([toggleName, detailsName]) => {
      if (name !== toggleName || getPath(values, toggleName) === "true") return;

      clearErrors(detailsName);
      setValue(detailsName, "", {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: false,
      });
    });

    if (name === businessAnyDegreesName) {
      clearErrors(businessDegreesName);

      if (getPath(values, businessAnyDegreesName) === "true") {
        if ((getPath(values, businessDegreesName) || []).length === 0) {
          setValue(businessDegreesName, [{ ...businessDegreeDefaultValue }], {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: false,
          });
        }
      } else {
        setValue(businessDegreesName, [], {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: false,
        });
      }
    }

    if (name === businessAnyTradingHistoryName) {
      clearErrors(businessTradingHistoryName);

      if (getPath(values, businessAnyTradingHistoryName) !== "true") {
        setValue(businessTradingHistoryName, [], {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: false,
        });
      }
    }

    const professionalClientStage = stages.find((stage) =>
      stage.fields.some((field) => field.name === deemedName),
    );
    const dependentFields = professionalClientStage.fields
      .map((field) => field.name)
      .filter((fieldName) => fieldName && fieldName !== deemedName);

    if (name === deemedName) {
      dependentFields.forEach((fieldName) => {
        if (!fieldIsHidden(fieldName, values, getPath)) return;

        clearErrors(fieldName);
        setValue(fieldName, fieldName.endsWith("_documents") ? [] : "", {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: false,
        });
      });
    }

    [
      [
        anyExperienceName,
        [
          `${professionalClientName}.experience`,
          `${professionalClientName}.experience_documents`,
        ],
      ],
      [
        financialName,
        [`${professionalClientName}.financial_documents`],
      ],
      [
        anyRelatedName,
        [
          `${professionalClientName}.related_relationship`,
          `${professionalClientName}.related_documents`,
        ],
      ],
    ].forEach(([toggleName, fieldNames]) => {
      if (name !== toggleName || getPath(values, toggleName) === "true") return;

      fieldNames.forEach((fieldName) => {
        clearErrors(fieldName);
        setValue(fieldName, fieldName.endsWith("_documents") ? [] : "", {
          shouldDirty: true,
          shouldTouch: true,
          shouldValidate: false,
        });
      });
    });
  },
};

document.addEventListener(
  "DOMContentLoaded",
  () => mountOnboardingForm("kyb", config),
  false,
);
